import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "overflow-hidden h-screen flex flex-col" }
const _hoisted_2 = { class: "pr-4 pb-4 bg-white flex justify-between items-center sticky top-0 z-10 border-b max-h-full" }
const _hoisted_3 = { class: "flex gap-4 items-center ml-5 mt-5" }
const _hoisted_4 = { class: "font-extrabold text-gray-900 text-2xl md:text-3xl" }
const _hoisted_5 = {
  class: "rounded-xl px-3 py-1.5 text-gray-900 text-sm leading-none font-medium",
  style: {"background":"linear-gradient(36deg, #ff9f10, #fcff00)"}
}
const _hoisted_6 = {
  key: 0,
  class: "flex gap-3 items-center sm:mt-4"
}
const _hoisted_7 = {
  key: 0,
  class: "flex flex-1 flex-col xl:flex-row overflow-auto xl:overflow-hidden"
}
const _hoisted_8 = { class: "flex content-start flex-1 flex-wrap flex-col sm:flex-row gap-4 p-4 xl:overflow-auto" }
const _hoisted_9 = { class: "flex gap-4 flex-col flex-[3] sm:min-w-[550px] w-full" }
const _hoisted_10 = { class: "bg-white px-2 py-3 border border-gray-200 shadow rounded-lg relative" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { class: "text-lg font-bold text-gray-900 mb-2 text-center" }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "text-lg font-bold text-gray-900 mb-2 text-center" }
const _hoisted_15 = {
  key: 2,
  class: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold text-xl"
}
const _hoisted_16 = {
  key: 3,
  class: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
}
const _hoisted_17 = {
  key: 1,
  class: "flex-1 justify-center items-center flex"
}
const _hoisted_18 = {
  key: 2,
  class: "flex-1 justify-center items-center flex"
}
const _hoisted_19 = { class: "flex flex-col items-center justify-center text-gray-500 text-lg gap-2" }

import { TrashIcon } from "@heroicons/vue/24/outline";
import { EllipsisVerticalIcon } from "@heroicons/vue/24/solid";
import { useCurrentCustomerName, useCurrentSiteId } from "@oculai/planner";
import format from "date-fns/format";
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteLeave } from "vue-router";
import { segmentAnalytics } from "@/assets/scripts";
import BasicMenu from "@/components/UI/BasicMenu.vue";
import LoadingSpinner from "@/components/UI/LoadingSpinner.vue";
import MainButton from "@/components/UI/MainButton.vue";
import { useHasProjectFeature } from "@/composables/project";
import { useCustomToast } from "@/composables/toast";
import { useConfirmationModal, useSaveBeforeLeaveConfirmationModal } from "@/composables/toast";
import OpsProcessesRepository from "@/repositories/OpsProcessesRepository";
import PlannerRepository from "@/repositories/PlannerRepository";
import { HierarchyTagStore } from "@/types/HierarchyTag";
import { ProcessReport, WorkingHourCurveReport, CycleTimesReport } from "@/types/Process";
import NavigationComponent from "@/views/partials/NavigationComponent.vue";
import CycleTimesPlot from "./CycleTimesPlot.vue";
import CycleTimesPopover from "./CycleTimesPopover.vue";
import Form from "./Form/Form.vue";
import Sidebar from "./Sidebar.vue";
import WorkingHourCurvePlot from "./WorkingHourCurvePlot.vue";
import WorkingHourCurvePopover from "./WorkingHourCurvePopover.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Reports',
  setup(__props) {

const toast = useCustomToast();
const { t } = useI18n();
const customerName = useCurrentCustomerName();
const siteId = useCurrentSiteId();
const workingHoursFeatureEnabled = useHasProjectFeature("working_hours");
const showSaveBeforeLeaveConfirmationModal = useSaveBeforeLeaveConfirmationModal();
const showConfirmationModal = useConfirmationModal();

const hierarchyTags = ref<HierarchyTagStore[]>([]);
const reports = ref([] as ProcessReport[]);
const getHierarchyTagById = ref<Record<string, HierarchyTagStore>>({});
const selectedReport = ref<ProcessReport>();
const nextReportToSwitch = ref<ProcessReport>();
const reportSummary = ref<CycleTimesReport | WorkingHourCurveReport>();
const reportForm = ref();
const isLoadingReports = ref(false);

onMounted(async () => {
  isLoadingReports.value = true;
  segmentAnalytics("reports_overview");

  const promises = [
    getHierarchyTags(),
    OpsProcessesRepository.loadProcessReports(customerName, siteId),
  ];

  Promise.all(promises).then(async (values) => {
    const [promiseTags, promiseReports] = values as [HierarchyTagStore[], ProcessReport[]];

    hierarchyTags.value = promiseTags;
    reports.value = promiseReports.filter(
      (report) => report.plot.type !== "working_hour_curve" || workingHoursFeatureEnabled,
    );

    getHierarchyTagById.value = promiseTags.reduce((acc, tag) => {
      acc[tag._id] = tag;
      return acc;
    }, {} as Record<string, HierarchyTagStore>);

    if (reports.value.length) {
      const firstReport = structuredClone(reports.value[0]);
      selectedReport.value = firstReport;

      requestReportSummary(firstReport.plot.type, {
        processes: firstReport.processes,
        building: firstReport.building,
        level: firstReport.level,
        section: firstReport.section,
        ...firstReport.plot.config,
      });
    }

    isLoadingReports.value = false;
  });
});

onBeforeRouteLeave((to, from, next) => {
  if (!reportHasChanges()) {
    return next();
  }
  showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
    if (confirmed) {
      next();
    }
  });
});

const mapIds = (item: string | ProcessReport["level"][number]) => {
  return typeof item === "string" ? item : item._id;
};

const getHierarchyTags = async () => {
  const data = await PlannerRepository.loadHierarchyTags(customerName, siteId);

  data.sort((a, b) => a.number - b.number);

  return data;
};

const deepReportCompare = (obj1: unknown, obj2: unknown, isConfig = false): boolean => {
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return obj1 === obj2;
  }

  if (obj1 === null || obj2 === null) {
    return obj1 === obj2;
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepReportCompare(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  for (const key of Object.keys(obj1)) {
    // check for the new config field updates
    const obj1Config = obj1 as Record<string, unknown>;
    const obj2Config = obj2 as Record<string, unknown>;

    if (isConfig && (obj1Config[key] === undefined || obj2Config[key] === undefined)) {
      continue;
    }

    if (!deepReportCompare(obj1Config[key], obj2Config[key], key === "config")) {
      return false;
    }
  }

  return true;
};

const reportHasChanges = () => {
  if (!selectedReport.value) {
    return false;
  }

  if (!selectedReport.value?._id) {
    return true;
  }

  const firstReportToCompare = reports.value.find((item) => item._id === selectedReport.value?._id);
  const selectedReportFormValues = reportForm.value?.getValues();

  if (!firstReportToCompare || !selectedReportFormValues) {
    return false;
  }

  const prevReport = {
    name: firstReportToCompare.name,
    building: firstReportToCompare.building.map(mapIds),
    level: firstReportToCompare.level.map(mapIds),
    section: firstReportToCompare.section.map(mapIds),
    processes: firstReportToCompare.processes,
    plot: firstReportToCompare.plot,
  };

  const prevReportChanges = {
    name: selectedReportFormValues.name,
    building: selectedReportFormValues.building,
    level: selectedReportFormValues.level,
    section: selectedReportFormValues.section,
    processes: selectedReportFormValues.processes,
    plot: selectedReportFormValues.plot,
  };

  return !deepReportCompare(prevReport, prevReportChanges);
};

const handleSwitchReport = async (report?: ProcessReport, force = false) => {
  if (!report) {
    return;
  }

  if (!force && reportHasChanges()) {
    nextReportToSwitch.value = report;
    const isConfirmed = await showConfirmationModal({
      header: t("err.save_before_leave_title"),
      message: t("err.save_before_mode_switch"),
      confirmAction: selectedReport.value?._id
        ? t("buttons.revert_changes")
        : t("analytics.reports.dont_save_report"),
      cancelAction: t("buttons.stay_page"),
    });

    if (isConfirmed) {
      handleSwitchReport(report, true);
    }
    return;
  }

  reports.value = reports.value.filter((item) => item._id);
  selectedReport.value = structuredClone(report);

  requestReportSummary(report.plot.type, {
    processes: report.processes,
    building: report.building,
    level: report.level,
    section: report.section,
    ...report.plot.config,
  });
};

const handleUpdateReport = (report: Partial<ProcessReport> & { processes: number[] }) => {
  if (!selectedReport.value) {
    return;
  }

  selectedReport.value = {
    ...selectedReport.value,
    ...report,
  };
};

const handleSaveReport = async (report: ProcessReport) => {
  if (!selectedReport.value) {
    return;
  }

  const payload = {
    name: report.name,
    processes: report.processes,
    building: report.building,
    level: report.level,
    section: report.section,
    plot: report.plot,
  };

  let updatedReport = {} as ProcessReport;

  if (selectedReport.value._id) {
    updatedReport = await OpsProcessesRepository.updateProcessReport(
      customerName,
      siteId,
      selectedReport.value._id,
      payload,
    );
  } else {
    updatedReport = await OpsProcessesRepository.createProcessReport(customerName, siteId, payload);

    reports.value[reports.value.length - 1] = updatedReport;
    selectedReport.value = updatedReport;

    segmentAnalytics(`reports_create_${report.plot.type}`);
  }

  reports.value = reports.value.map((item) => {
    if (item._id !== updatedReport._id) {
      return item;
    }

    const fieldsToPopulate = ["building", "level", "section"] as const;

    fieldsToPopulate.forEach((field) => {
      updatedReport[field] = updatedReport[field].map((id) => {
        return getHierarchyTagById.value[
          id as unknown as string
        ] as unknown as ProcessReport[(typeof fieldsToPopulate)[number]][number];
      });
    });

    return updatedReport;
  });

  toast.success(t("analytics.reports.report_updated", { name: updatedReport.name }));
};

const requestReportSummary = async (
  plotType: ProcessReport["plot"]["type"],
  filter: Partial<{
    processes: number[] | ProcessReport["processes"];
    building: string[] | ProcessReport["building"];
    level: string[] | ProcessReport["level"];
    section: string[] | ProcessReport["section"];
    [key: string]: unknown;
  }>,
) => {
  const query: Record<string, unknown> = {};

  if (filter.building) query["building"] = filter.building.map(mapIds);
  if (filter.level) query["level"] = filter.level.map(mapIds);
  if (filter.section) query["section"] = filter.section.map(mapIds);
  if (filter.end_date) query["end_date"] = filter.end_date;
  if (filter.start_date) {
    // start date may be a timerange or a datestring
    if (typeof filter.start_date === "number") {
      query["start_date"] = format(new Date().getTime() - filter.start_date, "yyyy-MM-dd");
    } else {
      query["start_date"] = filter.start_date;
    }
  }
  if (filter.processes) {
    if (Array.isArray(filter.processes)) {
      query["processes"] = filter.processes;
    } else if (filter.processes.mode === "single_process") {
      query["processes"] = filter.processes.single_processes;
    } else {
      query["processes"] = filter.processes.components.flatMap((component) => component.processes);
    }
  }

  reportSummary.value = undefined;

  const data = await OpsProcessesRepository.loadReportProcessSummary(
    customerName,
    siteId,
    plotType,
    query,
  );

  reportSummary.value = data;
};

const handleRefetchForm = (data: Record<string, unknown>) => {
  if (!selectedReport.value) {
    return;
  }

  requestReportSummary(selectedReport.value.plot.type as ProcessReport["plot"]["type"], data);
};

const handleCreateReport = async (plot_type: ProcessReport["plot"]["type"]) => {
  const hasUnsavedReport = reports.value.length && !reports.value.at(-1)?._id;

  if (hasUnsavedReport) {
    toast.error(t("analytics.reports.save_report_before_create"));
    return;
  }

  let reportName = t(`analytics.reports.${plot_type}_plot`);

  const lastNumber = reports.value
    .filter((group) => group.name.startsWith(reportName))
    .map((group) => {
      const number = group.name.replace(reportName, "").trim();

      return number.length > 0 ? parseInt(number, 10) : 0;
    })
    .sort((a, b) => b - a)[0];

  if (lastNumber || lastNumber === 0) {
    reportName += ` ${lastNumber + 1}`;
  }

  let plotConfig = {};

  if (plot_type === "cycle_times") {
    plotConfig = {
      show_planned: true,
      show_utilization: workingHoursFeatureEnabled,
      show_outages: true,
      show_active_days: true,
      show_inactive_days: true,
      aggregation_level: "section",
    };
  } else if (plot_type === "working_hour_curve") {
    plotConfig = {
      start_date: 30 * 24 * 60 * 60 * 1000,
      end_date: undefined,
      show_mean: true,
    };
  }

  const newReport = {
    name: reportName,
    processes: { mode: "component", single_processes: [], components: [] },
    building: [],
    level: [],
    section: [],
    plot: {
      type: plot_type,
      config: plotConfig,
    },
  } as unknown as ProcessReport;

  selectedReport.value = structuredClone(newReport);
  reports.value.push(newReport);

  requestReportSummary(newReport.plot.type, {
    processes: [],
    building: [],
    level: [],
    section: [],
    ...newReport.plot.config,
  });
};

const handleDeleteReport = async () => {
  if (!selectedReport.value) {
    return;
  }

  reports.value = reports.value.filter((report) => report._id !== selectedReport.value?._id);

  await OpsProcessesRepository.deleteProcessReport(customerName, siteId, selectedReport.value._id);
  toast.success(t("analytics.reports.report_deleted", { name: selectedReport.value.name }));
  segmentAnalytics("reports_delete");

  if (!reports.value.length) {
    selectedReport.value = undefined;

    return;
  }

  selectedReport.value = reports.value[0];

  requestReportSummary(selectedReport.value.plot.type, {
    processes: selectedReport.value.processes,
    building: selectedReport.value.building,
    level: selectedReport.value.level,
    section: selectedReport.value.section,
    ...selectedReport.value.plot.config,
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(NavigationComponent, {
    activeTag: _unref(t)('analytics.reports.reports'),
    hideBreadcrumbs: true
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t("analytics.reports.my_reports")), 1),
            _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t("app_features.beta")), 1)
          ]),
          (selectedReport.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(MainButton, {
                  label: _unref(t)('buttons.save'),
                  type: "button",
                  color: "yellow",
                  class: "sm:mr-0",
                  onClicked: reportForm.value?.handleSubmit
                }, null, 8, ["label", "onClicked"]),
                _createVNode(BasicMenu, { position: "left" }, {
                  icon: _withCtx(() => [
                    _createVNode(_unref(EllipsisVerticalIcon), {
                      class: "h-8 w-8",
                      "aria-hidden": "true"
                    })
                  ]),
                  content: _withCtx(({ item }) => [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(item), { onClick: handleDeleteReport }, {
                      default: _withCtx(({ active }) => [
                        _createElementVNode("p", {
                          class: _normalizeClass([
                    active ? 'bg-gray-100 text-red-600' : 'text-gray-700',
                    'px-4 py-2 cursor-pointer whitespace-nowrap flex items-center',
                  ])
                        }, [
                          _createVNode(_unref(TrashIcon), { class: "h-4 w-4 mr-1" }),
                          _createElementVNode("span", null, _toDisplayString(_unref(t)("analytics.reports.remove_report")), 1)
                        ], 2)
                      ]),
                      _: 2
                    }, 1024))
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        (reports.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(Sidebar, {
                reports: reports.value,
                currentReport: selectedReport.value,
                onHandleSwitchReport: handleSwitchReport,
                onHandleCreateReport: handleCreateReport
              }, null, 8, ["reports", "currentReport"]),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(Form, {
                  selectedReport: selectedReport.value,
                  hierarchyTags: hierarchyTags.value,
                  onOnChange: handleUpdateReport,
                  onOnRefetch: handleRefetchForm,
                  onOnSave: handleSaveReport,
                  class: "flex-1 min-w-[350px] w-full",
                  ref_key: "reportForm",
                  ref: reportForm
                }, null, 8, ["selectedReport", "hierarchyTags"]),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    (selectedReport.value?.plot.type === 'cycle_times')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _createElementVNode("h3", _hoisted_12, _toDisplayString(_unref(t)("analytics.reports.cycle_times_plot")), 1),
                          _createVNode(CycleTimesPopover, { class: "absolute top-4 right-4" }),
                          _createVNode(CycleTimesPlot, {
                            report: selectedReport.value,
                            data: reportSummary.value as CycleTimesReport || [],
                            hierarchy: hierarchyTags.value
                          }, null, 8, ["report", "data", "hierarchy"])
                        ]))
                      : _createCommentVNode("", true),
                    (selectedReport.value?.plot.type === 'working_hour_curve')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                          _createElementVNode("h3", _hoisted_14, _toDisplayString(_unref(t)("analytics.reports.working_hour_curve_plot")), 1),
                          _createVNode(WorkingHourCurvePopover, { class: "absolute top-4 right-4" }),
                          _createVNode(WorkingHourCurvePlot, {
                            report: selectedReport.value,
                            data: reportSummary.value as WorkingHourCurveReport || {}
                          }, null, 8, ["report", "data"])
                        ]))
                      : _createCommentVNode("", true),
                    (reportSummary.value && !Object.keys(reportSummary.value).length)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(_unref(t)("analytics.reports.no_data")), 1))
                      : _createCommentVNode("", true),
                    (!reportSummary.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _createVNode(LoadingSpinner)
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        (isLoadingReports.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
              _createVNode(LoadingSpinner)
            ]))
          : _createCommentVNode("", true),
        (!isLoadingReports.value && !reports.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("p", null, _toDisplayString(_unref(t)("analytics.reports.no_reports")), 1),
                _createVNode(MainButton, {
                  label: _unref(t)('analytics.reports.create_first_report'),
                  type: "button",
                  color: "yellow",
                  class: "sm:mr-0 mt-2",
                  onClicked: _cache[0] || (_cache[0] = ($event: any) => (handleCreateReport('cycle_times')))
                }, null, 8, ["label"])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["activeTag"]))
}
}

})